import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import Logo from './Logo';
import { PILLARS } from '../../data/editorial';
import { NAV, LANGS } from '../../data/mock';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

export default function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(false);
  const lang = LANGS.find((l) => l.code !== 'en' && pathname.startsWith(`/${l.code}`)) || LANGS[0];
  const isFr = pathname.startsWith('/fr');
  const genChildren = isFr
    ? [
      ...PILLARS.map(p => ({ label: p.title, to: p.path })),
      { label: 'Écriture cursive', to: '/fr/ecriture-cursive' },
      { label: 'Texte bizarre', to: '/fr/texte-bizarre' },
      { label: 'Polices Discord', to: '/fr/polices-discord' },
      { label: 'Générateur de bio Instagram', to: '/fr/generateur-bio-instagram' },
    ]
    : NAV[0].children;
  const FR_MAP = { '/blogs': ['/fr/blog', 'Blog'], '/about-us': ['/fr/a-propos', 'À propos'], '/contact-us': ['/fr/contact', 'Contact'] };
  const navTo = (n) => (isFr && FR_MAP[n.to] ? FR_MAP[n.to][0] : n.to);
  const navLabel = (n) => (isFr && FR_MAP[n.to] ? FR_MAP[n.to][1] : n.label);
  const genActive = ['/', '/weird-text-generator', '/discord-font-generator', '/instagram-bio-generator', '/fr/ecriture-cursive', '/fr/texte-bizarre', '/fr/polices-discord', '/fr/generateur-bio-instagram'].includes(pathname);

  return (
    <header className="site-header sticky top-0 z-40" data-testid="site-header">
      <div className="header-inner">
        <Logo to={lang.to} />
        <nav className="hidden md:flex items-center gap-8">
          <div className="relative group">
            <Link to={isFr ? '/fr/ecriture-cursive' : '/'} className={`nav-link inline-flex items-center gap-4 ${genActive ? 'active' : ''}`} data-testid="nav-text-generators">
              {isFr ? 'Générateurs' : 'Text Generators'} <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
            </Link>
            <div className="absolute left-0 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-200 z-50">
              <div className="bg-white shadow-xl rounded-md border border-slate-100 py-2 w-[260px]">
                {genChildren.map((c) => (
                  <NavLink key={c.to} to={c.to} data-testid={`nav-${c.to.slice(1)}`}
                    className={({ isActive }) => `block px-5 py-2.5 text-[14px] transition-colors ${isActive ? 'text-[var(--brand)]' : 'text-slate-600 hover:text-[var(--brand)] hover:bg-slate-50'}`}>
                    {c.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
          {NAV.slice(1).map((n) => {
            const to = navTo(n);
            const blogActive = n.to === '/blogs' && (pathname.startsWith('/blogs') || pathname.startsWith('/fr/blog'));
            return (
              <NavLink key={n.to} to={to} data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) => `nav-link ${isActive || blogActive ? 'active' : ''}`}>
                {navLabel(n)}
              </NavLink>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-2 outline-none text-slate-500 hover:text-slate-800" data-testid="lang-switcher">
              <span className="text-lg leading-none">{lang.flag}</span>
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[170px]">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} asChild>
                  <Link to={l.to} className="flex items-center gap-2 cursor-pointer" data-testid={`lang-${l.code}`}>
                    <span className="text-lg">{l.flag}</span> {l.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
        <button className="md:hidden p-2 text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white px-5 pb-4 fade-up" data-testid="mobile-menu">
          <button className="w-full flex justify-between items-center py-3 text-slate-700 font-medium" onClick={() => setSub(!sub)}>
            {isFr ? 'Générateurs' : 'Text Generators'} <ChevronDown className={`w-4 h-4 transition-transform ${sub ? 'rotate-180' : ''}`} />
          </button>
          {sub && (
            <div className="pl-4">
              {!isFr && <Link to="/" onClick={() => setOpen(false)} className="block py-2 text-slate-600">Cursive Text Generator</Link>}
              {genChildren.map((c) => (
                <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block py-2 text-slate-600" data-testid={`mobile-nav-${c.to.replace(/\//g, '-')}`}>{c.label}</Link>
              ))}
            </div>
          )}
          {NAV.slice(1).map((n) => (
            <Link key={n.to} to={navTo(n)} onClick={() => setOpen(false)} className="block py-3 text-slate-700 font-medium border-t border-slate-50">{navLabel(n)}</Link>
          ))}
          <div className="flex gap-4 pt-3 border-t border-slate-50">
            {LANGS.map((l) => (
              <Link key={l.code} to={l.to} onClick={() => setOpen(false)} className="text-sm text-slate-600">{l.flag} {l.label}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
